/* eslint-disable no-tabs */
import { forwardRef, FunctionComponent } from 'react';
import { useTranslation } from 'react-i18next';

import { useApplicationUser } from 'entities/application/modal/useApplicationUser';
import { Plan } from 'entities/application/types';
import { useTariffs } from 'entities/tariff/model/useTariffs';
import { FreeTariffCard } from 'entities/tariff/ui/FreeTariffCard/FreeTariffCard';
import { TariffsListItem } from 'entities/tariff/ui/TariffsListItem/TariffsListItem';
import { useAuthorization } from 'entities/user/hooks/useAuthorization';
import { MpBlock } from 'shared/ui/MpBlock';
import { MpTitle } from 'shared/ui/MpTitle';
import { TariffsDetailsContainerStyled, TariffsListStyled } from './TariffsList.styles';

type PlansListProps = {
	applicationId: string;
	handleInstall: () => void;
	plans: Plan[];
	updateActiveSubscriptionsStatus: () => void;
	redirectUrl: string;
	handleFreeInstall: () => void;
	hasActiveSubscriptions: boolean;
	tariffsDetails?: FunctionComponent;
};

export const TariffsList = forwardRef<HTMLElement, PlansListProps>(
	(
		{
			applicationId,
			plans,
			handleInstall,
			updateActiveSubscriptionsStatus,
			redirectUrl,
			handleFreeInstall,
			hasActiveSubscriptions,
			tariffsDetails,
		},
		ref,
	) => {
		const applicationUser = useApplicationUser(applicationId);
		const authorization = useAuthorization();
		const internalAccount = authorization?.blockAppInstallation;
		const canPurchaseApps = authorization?.canPurchaseApps;
		const { t } = useTranslation();
		const tariffs = useTariffs(applicationId, !!canPurchaseApps);

		if (!tariffs.length) {
			return null;
		}

		return (
			<MpBlock ref={ref} id='tariffs'>
				<MpTitle level={2}>{t(`Marketplace_Tariffs`)}</MpTitle>
				<TariffsListStyled>
					{tariffs
						.filter(n => !n.isFree)
						.map(plan => (
							<TariffsListItem
								key={plan.id}
								applicationId={applicationId}
								tariff={plan}
								plansInfo={plans}
								handleInstall={handleInstall}
								updateActiveSubscriptionsStatus={updateActiveSubscriptionsStatus}
							/>
						))}
				</TariffsListStyled>

				{!!plans.filter(n => n.isFree).length && (
					<FreeTariffCard
						tariff={plans.filter(n => n.isFree)[0]}
						applicationId={applicationId}
						handleInstall={handleFreeInstall}
						redirectUrl={redirectUrl}
						disabled={hasActiveSubscriptions || !!internalAccount}
						isActive={!hasActiveSubscriptions && applicationUser?.status === `Installed`}
					/>
				)}
				{tariffsDetails && (
					<TariffsDetailsContainerStyled id='tariffs-details'>
						{tariffsDetails({})}
					</TariffsDetailsContainerStyled>
				)}
			</MpBlock>
		);
	},
);
